<template>
	<div class="message-edit" :class="{ 'media-margin': hasMedia }">
		<Editor
			noDraft
			:disabled="isLoading"
			noPlaceholder
			noClear
			:mentionUsers="mentionUsers"
			ref="messageEdit"
			v-model="message.message"
			@suggestionsChange="onSuggestionsChange"
			@enterPressed="saveChanges"
			@paste.stop
			@resolvingMentions="needsToResolveMentions = $event"
		/>

		<div class="emoji-selector">
			<EmojiPicker class="emojis" @change="insertEmoji">
				<FaceSmileIcon type="regular" />
			</EmojiPicker>
		</div>

		<div class="edit-actions">
			<MessageCharLimit
				:text="message.message"
				@over="isOverMax = true"
				@under="isOverMax = false"
			/>
			<Button
				:pronto="!isOverMax"
				:danger="isOverMax"
				:loading="isLoading"
				:disabled="isOverMax"
				@click="onClickCheck"
				class="save-btn"
			>
				<CheckIcon v-if="!isLoading" type="regular" />
			</Button>
			<Button @click="onCancel" class="cancel-btn">
				<CloseIcon type="regular" />
			</Button>
		</div>

		<DeleteModal
			v-if="confirmDelete"
			@close="closeDeleteModal"
			@delete="deleteMessage()"
			:bodyText="$t('chat_message_confirmdelete_title')"
			:title="$t('chat_message_confirmdelete_button')"
			:isProcessing="showDeleteSpinner"
		/>

		<StandardModal
			v-if="hasError"
			open
			small
			:title="$t('common_error')"
			:confirmText="$t('common_button_done')"
			:confirmProps="{ danger: true }"
			hideCancel
			@confirm="hasError = false"
			@cancel="hasError = false"
			class="error-details-modal"
		>
			<CircleExclamationIcon type="regular" halo />
			<div class="error-details-message">
				{{ $t('chat_edit_message_error_text') }}
			</div>
		</StandardModal>
	</div>
</template>

<script>
import Editor, {
	replaceMentions,
	ASYNC_MENTIONS_REGEXP,
} from '@/components/Editor'
import DeleteModal from '@/components/DeleteModal'
import { Button, StandardModal } from '@/components/common'
import { getMessage, setMessage, deleteMessage, getUser } from '@/store'
import { asyncIsElementInView } from '@/helperMethods'
import { post } from '@/helpers/api'
import MessageCharLimit from '@/components/MessageInput/MessageCharLimit'
import {
	CheckIcon,
	CloseIcon,
	FaceSmileIcon,
	CircleExclamationIcon,
} from '@/icons'
import { addEscapeListener, removeEscapeListener } from '@/helpers/escape'
import * as translations from '@/store/translations'
import EmojiPicker from '@/components/common/EmojiPicker'

export default {
	components: {
		Editor,
		DeleteModal,
		Button,
		CheckIcon,
		CloseIcon,
		EmojiPicker,
		FaceSmileIcon,
		StandardModal,
		CircleExclamationIcon,
		MessageCharLimit,
	},
	props: ['messageId'],

	data() {
		return {
			confirmDelete: false,
			message: null,
			isLoading: false,
			originalMessageText: '',
			showDeleteSpinner: false,
			hasError: false,
			mentionUsers: [],
			isOverMax: false,
			needsToResolveMentions: false,
		}
	},

	emits: ['hide', 'success', 'cancel'],

	created() {
		this.message = Object.assign({}, getMessage(this.messageId))
		this.originalMessageText = this.message.message

		if (this.hasMention) {
			this.message.message = replaceMentions(this.message.message)
		}

		this.escapeId = addEscapeListener(() => {
			this.onCancel()
		})

		this.focus()

		this.$onEventBus('user-update', this.updateMentionUsers)
		this.updateMentionUsers()
	},

	beforeUnmount() {
		removeEscapeListener(this.escapeId)
	},

	computed: {
		hasMention() {
			return (
				(this.message.mentions && this.message.mentions.length > 0) ||
				this.message.message.includes('<@everyone>')
			)
		},

		hasMedia() {
			return this.message.messagemedia?.length
		},

		hasTextChanged() {
			return this.message.message.trim() !== this.originalMessageText.trim()
		},
	},

	methods: {
		closeDeleteModal() {
			this.confirmDelete = false
			this.$emit('hide')
		},

		async deleteMessage() {
			this.showDeleteSpinner = true
			try {
				if (this.message.id) {
					await post('/api/v1/message.delete', { message_id: this.message.id })
				}
				deleteMessage(this.message.id || this.message.uuid)
				this.confirmDelete = false
			} catch (e) {
				console.error(e)
				this.$alert.error(e)
			} finally {
				this.showDeleteSpinner = false
			}
		},

		onClickCheck() {
			this.saveChanges()
		},

		async saveChanges() {
			if (this.isOverMax) return
			if (!this.hasTextChanged) {
				return this.onCancel()
			}

			const { id, message } = this.message
			if (
				message === '\n' &&
				this.originalMessageText &&
				!this.message.hasMedia &&
				!this.message.task
			) {
				this.confirmDelete = true
				return
			}

			const payload = {
				message_id: id,
				message: message.trim(),
			}
			if (this.needsToResolveMentions) {
				const containsPotentialMention = ASYNC_MENTIONS_REGEXP.test(
					payload.message
				)
				payload.replace_string_mentions = containsPotentialMention
			}

			try {
				this.isLoading = true
				const { data } = await post('/api/v1/message.edit', payload)
				translations.remove(id)
				setMessage({
					...data.message,
					isTranslated: false,
				})
				this.$emit('success')
				this.isLoading = false
				this.$nextTick(() => EventBus.$emit('focus-editor'))
			} catch (e) {
				console.error(e)
				this.$alert.error(e)
				this.isLoading = false
			}
		},

		onCancel() {
			this.$emit('cancel')
			this.$nextTick(() => EventBus.$emit('focus-editor'))
		},

		async focus() {
			await this.$nextTick()
			this.$refs.messageEdit.onFocus()
		},

		onSuggestionsChange(showing, type) {
			if (!showing) return

			// wait for suggestions to fully render...
			setTimeout(this.scrollSuggestionsIntoViewIfNeeded, 25, type)
		},

		async scrollSuggestionsIntoViewIfNeeded(type) {
			const messageList = document.querySelector('.message-list')
			const suggestions = this.$el.querySelector(
				type === 'mention' ? '.mention-completions' : '.emoji_completions'
			)

			if (
				!suggestions ||
				(await asyncIsElementInView(suggestions, messageList))
			)
				return

			suggestions.scrollIntoView({ behavior: 'smooth' })
		},

		insertEmoji(emoji) {
			this.$refs.messageEdit.insertEmoji(emoji.emoji)
			this.focus()
		},

		updateMentionUsers() {
			this.mentionUsers = this.message.mentions?.map(getUser)
		},
	},
}
</script>

<style scoped lang="scss">
@import 'src/sass/variables';

.message-edit {
	@include flex;

	:deep(.pronto-editor) {
		height: 40px;
		flex-grow: 1;
		position: relative;
		background: var(--neutral-0);
		border: 1px solid var(--neutral-200);
		border-radius: 5px;
		padding: 8px;
		max-width: calc(100% - 150px);
		transition: none !important;

		&.disabled {
			opacity: 0.7;
		}

		.mention-completions,
		.emoji_completions {
			width: calc(100% + 46px);
			left: 0;
			right: 0;
			bottom: 100%;
			max-width: none;
			border-bottom: 1px solid var(--neutral-200);
		}
	}

	.edit-actions {
		@include flex;
		margin-left: 8px;
		position: relative;

		:deep(.char-limit) {
			position: absolute;
			top: -40px;
			left: calc(50% - 20px);
		}
		.pronto-btn {
			@include flex-center;
			padding: 0;
			width: 43px;
			height: 39px;
			.pronto-icon {
				font-size: 18px;
			}
		}
	}
}

:deep(.emoji-picker-container) {
	.picker {
		right: -52px;
	}
}

.message-edit :deep(.ql-editor.ql-blank::before) {
	content: '' !important;
}

.message-edit :deep(.ql-editor) {
	user-select: text;
}

.emoji-selector {
	display: flex;
	align-self: stretch;
	align-items: center;
	padding: 0 12px 0 12px;
	border: 1px solid var(--neutral-200);
	border-left: 0;
	border-top-right-radius: 5px;
	border-bottom-right-radius: 5px;
	background: var(--neutral-0);
}

$svg-fill: #949598;
$svg-hover: #67686a;
$svg-active: #36c6ab;
.emojis {
	height: 21px;
	cursor: pointer;
	:deep(.emoji-mart) {
		&::before {
			right: 52px;
		}
	}
	&:hover {
		:deep(g) {
			fill: $svg-hover !important;
		}
	}
	&.open {
		:deep(svg) g {
			fill: $svg-active !important;
		}
	}

	.pronto-icon {
		cursor: pointer;
		color: $svg-fill;
		font-size: 21px;
		&:hover {
			color: $svg-hover;
		}
		&.open {
			color: $svg-active;
		}
	}
}

:deep(.pronto-editor) {
	border-top-right-radius: 0 !important;
	border-bottom-right-radius: 0 !important;
}

.error-details-modal {
	:deep(.modal-body) {
		padding: 24px 110px 24px 110px;
		@include flex-column-center;
		.pronto-icon-halo {
			color: var(--red-200);
			font-size: 55px;
			margin-bottom: 24px;
		}
		.error-details-message {
			font-size: 18px;
			margin-bottom: 16px;
			&:last-child {
				margin-bottom: 0;
			}
		}
	}
}

.media-margin {
	margin-top: 10px;
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.message-edit {
		.pronto-editor {
			background: var(--neutral-100);
		}
		.emoji-selector {
			background: var(--neutral-100);
		}
	}
}
</style>
